import React from 'react';
import { EllipsisVertical } from 'lucide-react';
import ActionIcon from './ActionIcon';
import cn from './primitives/cn';
import { spaceNum } from './tokens';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './primitives/dropdownMenu';

/**
 * The row-action menu: the three dots at the end of a table row, and the actions they open.
 *
 * Every table in the CMS draws its row actions through this one component: `ItemTable`
 * (Collections and every screen built on `CmsItemListSection`), `OrderedItemTable` (the
 * reorderable lists), and the resource, category, directory, calendar, book and slideshow
 * tables. Those last ones used to put a row of red and green buttons in the last cell, so
 * the same "do something to this row" was a kebab on one screen and three buttons on the
 * next.
 *
 * It replaces the kebab only Collections had, which lived in main.css as `.action-ellipsis`
 * and `.action-menu`. That one was a `<div>` with a click handler, so the keyboard could not
 * reach it, and its panel was absolutely positioned inside the cell, so a card with
 * `overflow: hidden` clipped the menu on the last rows. Radix renders the panel in a portal,
 * moves focus into it, closes it on Escape and flips it above the row when there is no room
 * below. It kept the old panel's width and rows.
 *
 * `modal={false}` because a non-modal menu behaves the way the old one did: a click outside
 * closes it and still lands on whatever was clicked. It also never sets
 * `pointer-events: none` on `<body>`, which a modal menu does, and which can be left behind
 * when a menu item opens a dialog in the same moment the menu closes.
 *
 * Destructive actions always go last, below a rule, whatever order a screen lists them in,
 * so Remove is never one slip away from Edit. An action is destructive when it says so, or
 * when its label starts with Remove or Delete (`isDestructiveAction` below).
 *
 * Each action is already resolved against its row:
 *   { label, icon, onSelect, disabled, destructive, wrap }
 * `icon` is a Font Awesome class string, as everywhere else in the CMS; `ActionIcon` draws
 * it in the rail's outline set. `wrap` lets the caller put something around one item: the
 * "publish in progress" hover card. Turning the app's row actions, whose label, icon and
 * flags can be functions of the row, into this shape is
 * `shared/itemTable/components/ItemActionList.js`'s job, not this one's.
 *
 * The look is Ryan's Collections mockup (2026-09-29): outline icons, a light fill under
 * the pointer, a brand ring on the item the keyboard is on, and Remove in the danger red.
 *
 * Redesign iteration 1 anchors the panel to the trigger: its right edge is the trigger's
 * right edge (`align="end"`) and its top is 4px under the trigger's bottom (`sideOffset`;
 * the primitive's own default is the rail picker's 6). While the menu is open the trigger
 * keeps the fill and the ink it has under the pointer, and Radix sets `aria-expanded`.
 */
/** The gap between the trigger's bottom and the panel's top. */
const MENU_OFFSET = spaceNum[4];
const TRIGGER = cn(
  'tw-inline-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-p-0',
  'tw-rounded-control tw-border-0 tw-bg-transparent tw-cursor-pointer',
  'tw-text-section tw-leading-none tw-text-muted',
  'hover:tw-bg-fill hover:tw-text-ink data-[state=open]:tw-bg-fill data-[state=open]:tw-text-ink',
  'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-brand-focus'
);

const PANEL = 'tw-z-menu tw-min-w-menu tw-flex tw-flex-col tw-gap-0.5 tw-border-line2 tw-shadow-md';

const ITEM = cn(
  'tw-group tw-h-menu tw-py-0 tw-whitespace-nowrap tw-font-normal',
  // Radix focuses the highlighted item, so `focus-visible` is the keyboard's item only:
  // the pointer gets the fill, the keyboard gets the fill and the ring.
  'focus-visible:tw-ring-2 focus-visible:tw-ring-inset focus-visible:tw-ring-brand-focus',
  // A Font Awesome fallback glyph sits at 0.9em, the size the menu's buttons used to draw it at.
  '[&>i]:tw-text-[0.9em]'
);
const SAFE_ITEM = 'data-[highlighted]:tw-bg-fill';
const DESTRUCTIVE_ITEM = 'tw-text-danger';

// Muted at rest and the row's own colour when the row is highlighted. A destructive row's
// icon is always its red.
const SAFE_ICON = 'tw-text-muted group-data-[highlighted]:tw-text-current';

const RULE = 'tw-my-1 tw-bg-line2';

/** A label that says the action throws something away: "Remove", "Delete Draft". */
const DESTRUCTIVE_LABEL = /^\s*(remove|delete)\b/i;

/**
 * Whether an action is destructive: red, and last below the rule.
 *
 * `destructive: true` says so outright. A screen that forgets the flag still gets the red
 * when its label starts with Remove or Delete (SURF-4821, SURF-4835 and SURF-4836 were
 * each a Remove drawn in the menu ink because its screen never set the flag). An explicit
 * `destructive: false` is the way out for a label that only reads as one.
 */
export const isDestructiveAction = ({ destructive, label } = {}) => {
  if (destructive !== undefined && destructive !== null) return !!destructive;
  return typeof label === 'string' && DESTRUCTIVE_LABEL.test(label);
};

const RowAction = ({ action }) => {
  const destructive = isDestructiveAction(action);
  const item = (
    <DropdownMenuItem
      destructive={destructive}
      disabled={action.disabled}
      onSelect={action.onSelect}
      className={cn(ITEM, destructive ? DESTRUCTIVE_ITEM : SAFE_ITEM)}
    >
      <ActionIcon icon={action.icon} className={cn(!destructive && SAFE_ICON)} />
      <span>{action.label}</span>
    </DropdownMenuItem>
  );
  return action.wrap ? action.wrap(item) : item;
};

const RowActions = ({ actions = [], label = 'Actions' }) => {
  if (!actions.length) return null;
  const safe = actions.filter((action) => !isDestructiveAction(action));
  const destructive = actions.filter(isDestructiveAction);

  return (
    // The kebab sits at the right-hand end of its cell, the way the old one floated there.
    <div className="row-actions tw-flex tw-justify-end">
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger className={cn('row-actions-trigger', TRIGGER)} aria-label={label}>
          <EllipsisVertical size={18} strokeWidth={2} aria-hidden="true" />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={MENU_OFFSET}
          className={cn('row-actions-menu', PANEL)}
        >
          {safe.map((action, index) => (
            <RowAction key={action.key ?? `safe-${index}`} action={action} />
          ))}
          {safe.length > 0 && destructive.length > 0 && <DropdownMenuSeparator className={RULE} />}
          {destructive.map((action, index) => (
            <RowAction key={action.key ?? `destructive-${index}`} action={action} />
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default RowActions;
